import React, { useState } from 'react'
import axios from 'axios'

import '../../incidentDetails.scss'
import ThreatFeedStatusMenu from './ThreatFeedStatusMenu'
import { formatThreatFeedDatetime } from '../../../utilities/datetime'
import { eventTypes } from '../../../utilities/eventTypes'
import { getStatusClass } from '../../../utilities/statusCSSClass'

// ===========================================================================
// Threat Feed — View Details popup (PIXMC-6500).
//
// Mockup modal (Option A): left User/Threat details table, right Screenshot / HTML / Vision
// tabs. Driven straight off the clicked all-events row (which already carries the screenshot
// and page-content URLs), so opening it dispatches nothing and does not touch the shared
// events loading state. Presentational; ThreatFeed owns the open/close state.
// ===========================================================================

const userName = (e) => e.osUserId || e.osComputerName || e.osuuid || e.email || 'NA'

// The HTML blob URL is DERIVED from markedScreenshotURL for AI / PIXM / Google-Form /
// Unknown statuses (and reported-phish), not the raw pageContentURL — same rule as
// incidentDetails.getPageContentURL. Using pageContentURL directly is why no HTML loaded.
const AI_OR_UNKNOWN = ['AI Suspicious', 'Unknown', 'Google Form Password', 'PIXM Verified']
const htmlUrlOf = (e) => {
  if (!e) return null
  if (e.type === eventTypes.reported_phish && e.markedScreenshotURL) return `${e.markedScreenshotURL.slice(0, -5)}.html`
  if (AI_OR_UNKNOWN.includes(e.status) && e.markedScreenshotURL) return `${e.markedScreenshotURL.slice(0, -12)}.html`
  return e.pageContentURL || null
}

const ThreatFeedDetailModal = ({ event, onClose, onStatusAction }) => {
  const [tab, setTab] = useState('shot')
  const [html, setHtml] = useState(null)
  const [htmlError, setHtmlError] = useState(false)

  if (!event) return null

  const shot = event.resizedScreenshotURL || event.screenshotURL
  const marked = event.markedScreenshotURL

  const showTab = (next) => {
    setTab(next)
    if (next === 'html' && html === null && !htmlError) {
      const url = htmlUrlOf(event)
      if (!url) {
        setHtmlError(true)
        return
      }
      axios
        .get(url)
        .then((r) => setHtml(typeof r.data === 'string' ? r.data : JSON.stringify(r.data, null, 2)))
        .catch(() => setHtmlError(true))
    }
  }

  return (
    <div
      className="tfModalOverlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="tfModal">
        <div className="tfModalHeader">
          <div>
            <h2 className="tfModalTitle">Threat detail</h2>
            <p className="tfModalSub">
              {(event.brands || 'Phishing')} · detected {formatThreatFeedDatetime(event.detectionTime)}
            </p>
          </div>
          <ThreatFeedStatusMenu event={event} onAction={onStatusAction} />
        </div>

        <div className="tfModalBody">
          <div className="tfDetailGrid">
            <div>
              {/* Reuses the legacy detail page's #detailsTable styling as-is (incidentDetails.scss)
                  so User / Brand / URL / Status read exactly like the legacy detail page. */}
              <table id="detailsTable">
                <tbody>
                  <tr><td colSpan={2}><b>User Details :</b></td></tr>
                  <tr><td>User</td><td><b>{userName(event)}</b></td></tr>
                  <tr><td>Machine ID</td><td>{event.osuuid || 'NA'}</td></tr>
                  <tr><td>Computer</td><td>{event.osComputerName || 'NA'}</td></tr>
                  <tr><td>Opened</td><td>{formatThreatFeedDatetime(event.detectionTime)}</td></tr>
                  <tr><td colSpan={2}><b>Threat Details :</b></td></tr>
                  <tr><td>Brand</td><td><b>{event.brands || 'NA'}</b></td></tr>
                  <tr><td>Status</td><td><span className={getStatusClass(event.status)}><b>{event.status}</b></span></td></tr>
                  <tr><td>IP</td><td>{event.domainIPAddress || 'NA'}</td></tr>
                  <tr><td>Url</td><td><span style={{ wordBreak: 'break-all' }}>{event.url}</span></td></tr>
                </tbody>
              </table>
            </div>

            <div>
              <div className="tfDTabs">
                <button className={`tfDTab ${tab === 'shot' ? 'tfDTabActive' : ''}`} onClick={() => showTab('shot')}>Screenshot</button>
                <button className={`tfDTab ${tab === 'html' ? 'tfDTabActive' : ''}`} onClick={() => showTab('html')}>HTML</button>
                <button className={`tfDTab ${tab === 'vision' ? 'tfDTabActive' : ''}`} onClick={() => showTab('vision')}>Vision Analysis</button>
              </div>

              {tab === 'shot' && (shot
                ? <img className="tfDShotImg" src={shot} alt="Detection screenshot" />
                : <div className="tfDShot">No screenshot</div>)}

              {tab === 'html' && (htmlError
                ? <div className="tfDShot">HTML not available</div>
                : html === null
                  ? <div className="tfDShot">Loading…</div>
                  : <pre className="tfDHtml">{html}</pre>)}

              {tab === 'vision' && (marked
                ? <img className="tfDShotImg" src={marked} alt="Vision analysis" />
                : <div className="tfDShot">No vision analysis</div>)}
            </div>
          </div>
        </div>

        <div className="tfModalFooter">
          <button className="tfCloseBtn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

export default ThreatFeedDetailModal
